/**
 * Authentication API endpoints
 */
import { fetchApi, API_BASE_URL } from './apiBase';
import { User } from '../types';

// Get current user
export async function getCurrentUser(): Promise<User> {
  return fetchApi<User>('/auth/me/');
}

// Logout
export async function logout(): Promise<void> {
  await fetchApi<{ message: string }>('/auth/logout/', { method: 'POST' });
}

// Email/Password Registration
export async function register(data: {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
}): Promise<{ message: string; user: User; requires_verification?: boolean; email_sent?: boolean }> {
  return fetchApi('/auth/register/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Email/Password Login
export async function loginEmail(email: string, password: string): Promise<{ message: string; user: User }> {
  return fetchApi('/auth/login/', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

// Password Reset Request
export async function requestPasswordReset(email: string): Promise<{ message: string }> {
  return fetchApi('/auth/password/reset/', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

// Confirm Password Reset
export async function confirmPasswordReset(uid: string, token: string, password: string): Promise<{ message: string }> {
  return fetchApi('/auth/password/reset/confirm/', {
    method: 'POST',
    body: JSON.stringify({ uid, token, password }),
  });
}

// Verify Email
export async function verifyEmail(uid: string, token: string): Promise<{ message: string; user: User }> {
  return fetchApi('/auth/email/verify/', {
    method: 'POST',
    body: JSON.stringify({ uid, token }),
  });
}

// Resend Verification Email
export async function resendVerification(email: string): Promise<{ message: string; email_sent?: boolean }> {
  return fetchApi('/auth/email/resend-verification/', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

// Change Password
export async function changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
  return fetchApi('/auth/password/change/', {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
}

// OAuth Login URLs
export function getGoogleLoginUrl(): string {
  return `${API_BASE_URL}/auth/google/login/`;
}

// Incremental OAuth — asks for calendar scopes for the signed-in user
export function getGoogleCalendarConnectUrl(): string {
  return `${API_BASE_URL}/auth/google/connect-calendar/`;
}

export function getMicrosoftLoginUrl(): string {
  return `${API_BASE_URL}/auth/microsoft/login/`;
}

export function getAppleLoginUrl(): string {
  return `${API_BASE_URL}/auth/apple/login/`;
}

// Delete Account
export async function deleteAccount(): Promise<{ message: string }> {
  return fetchApi('/auth/account/delete/', {
    method: 'DELETE',
  });
}
